import type { Role } from '@/features/auth/roles';

/**
 * Every page in the Ommri Care Admin v2 sidebars, keyed once (pages shared by
 * several roles appear once, as in the artifact). Labels are the prototype's
 * sidebar labels. `task` notes which Riverlens task first builds the page, where
 * one is planned; everything else is outside the 3-day MVP.
 */
export interface PageDef {
  id: string;
  path: string;
  label: string;
  /** Roles that may open a direct-only page which is not shown in a sidebar. */
  directRoles?: Role[];
  /** Contextual clinical editors can opt out of the sidebar application shell. */
  layout?: 'full-screen';
  /**
   * API capability (resource:action) needed to open the page; null = any
   * signed-in staff member. Must match ommri-care-api's rbac/catalog.ts
   * (checked against api-catalog.snapshot.ts in tests).
   */
  permission: string | null;
  task?: string;
}

const page = (
  id: string,
  path: string,
  label: string,
  permission: string | null,
  task?: string,
  layout?: PageDef['layout'],
  directRoles?: Role[],
): PageDef => ({ id, path, label, permission, task, layout, directRoles });

export const PAGES = {
  dashboard: page('dashboard', '/dashboard', 'Dashboard', null),
  // Front desk
  queue: page('queue', '/queue', 'Queue & arrivals', 'queue:read', 'RL-549'),
  calendar: page(
    'calendar',
    '/calendar',
    'Calendar & bookings',
    'calendar:read',
    'RL-549',
  ),
  // RL-1666 (D7, review): full screen, no shell, sidebar or search. Only a
  // display account (exactly waiting_room:read) skips the idle lock here.
  waitingRoom: page(
    'waitingRoom',
    '/waiting-room',
    'Waiting-room screen',
    'waiting_room:read',
    'RL-1666',
    'full-screen',
  ),
  registerPatient: page(
    'registerPatient',
    '/patients/register',
    'Register patient',
    'patients:register',
    'RL-548',
  ),
  patients: page(
    'patients',
    '/patients',
    'Patients',
    'patients:read',
    'RL-548',
  ),
  billing: page('billing', '/billing', 'Billing', 'billing:read', 'RL-552'),
  governmentSchemes: page(
    'governmentSchemes',
    '/government-schemes',
    'Government schemes',
    'government_schemes:read',
  ),
  cashDrawer: page(
    'cashDrawer',
    '/cash-drawer',
    'Cash drawer',
    'cash_drawer:read',
  ),
  medicalCertificates: page(
    'medicalCertificates',
    '/medical-certificates',
    'Medical certificates',
    'medical_certificates:read',
  ),
  complaints: page(
    'complaints',
    '/complaints',
    'Complaints',
    'complaints:read',
  ),
  messages: page('messages', '/messages', 'Messages', 'patient_messages:read'),
  medicals: page(
    'medicals',
    '/medicals',
    'Medicals & documents',
    'medicals:read',
  ),
  // Doctor
  inbox: page('inbox', '/inbox', 'My inbox', 'doctor_inbox:read'),
  doctorsInbox: page(
    'doctorsInbox',
    '/doctors-inbox',
    'Doctors’ inbox',
    'doctor_inbox:oversee',
    'RL-1158',
  ),
  myPatients: page(
    'myPatients',
    '/my-patients',
    'My patients today',
    'my_patients:read',
    'RL-550',
  ),
  consultation: page(
    'consultation',
    '/consultations/:id',
    'Consultation',
    'encounters:read',
    'RL-1499',
    'full-screen',
  ),
  referralLetter: page(
    'referralLetter',
    '/consultations/:id/referral',
    'Referral letter',
    'encounters:read',
    'RL-1062',
    'full-screen',
  ),
  mySchedule: page(
    'mySchedule',
    '/my-schedule',
    'My schedule',
    null,
    'RL-1053',
  ),
  attendance: page(
    'attendance',
    '/attendance',
    'Attendance',
    'attendance:read_own',
    'RL-1054',
  ),
  myContract: page(
    'myContract',
    '/my-contract',
    'My contract',
    'locum_contracts:read_own',
    'RL-1055',
  ),
  myLocumPay: page(
    'myLocumPay',
    '/my-locum-pay',
    'My locum pay',
    'locum_pay:read_own',
    'RL-1056',
  ),
  myRevenue: page(
    'myRevenue',
    '/my-revenue',
    'My revenue',
    'revenue:read_own',
    'RL-1063',
  ),
  teleconsults: page(
    'teleconsults',
    '/teleconsults',
    'Teleconsults',
    'teleconsults:read',
  ),
  videoConsults: page(
    'videoConsults',
    '/video-consults',
    'Video consults',
    'teleconsults:read',
    'RL-1142',
  ),
  resultsToReview: page(
    'resultsToReview',
    '/results',
    'Results to review',
    'results:review',
    'RL-551',
  ),
  chronicCare: page(
    'chronicCare',
    '/chronic-care',
    'Chronic care',
    'chronic_care:read',
  ),
  mothersChildren: page(
    'mothersChildren',
    '/mothers-children',
    'Mothers & children',
    'mothers_children:read',
  ),
  safety: page('safety', '/safety', 'Safety & public health', 'safety:read'),
  // Mental health
  sessions: page(
    'sessions',
    '/wellbeing/sessions',
    'Sessions',
    'wellbeing_sessions:read',
  ),
  compartmentAccess: page(
    'compartmentAccess',
    '/wellbeing/access',
    'Compartment access',
    'wellbeing_access:read',
  ),
  // RL-1876 (gap #91): session types and practitioners, run by the practitioners.
  wellbeingSetup: page(
    'wellbeingSetup',
    '/wellbeing/setup',
    'Session types & practitioners',
    'wellbeing_access:manage',
    'RL-1876',
  ),
  // Lab
  labOrders: page(
    'labOrders',
    '/lab/orders',
    'Lab orders',
    'lab_orders:read',
    'RL-551',
  ),
  labResults: page(
    'labResults',
    '/lab/results',
    'Results inbox',
    'lab_results:read',
    'RL-551',
  ),
  // Dispensary
  prescriptions: page(
    'prescriptions',
    '/dispensary/prescriptions',
    'Prescriptions',
    'dispensary:read',
    'RL-551',
  ),
  stock: page('stock', '/stock', 'Stock & expiry', 'stock:read'),
  medicineLeaflets: page(
    'medicineLeaflets',
    '/dispensary/leaflets',
    'Medicine leaflets',
    'medicine_leaflets:read',
    'RL-1301',
  ),
  controlledDrugs: page(
    'controlledDrugs',
    '/controlled-drugs',
    'Controlled-drug register',
    'dispensary:read',
    'RL-1051',
    undefined,
    ['dispenser'],
  ),
  // Finance
  panels: page(
    'panels',
    '/finance/panels',
    'Panels & contracts',
    'panels:read',
  ),
  panelLimits: page(
    'panelLimits',
    '/finance/panel-limits',
    'Panel limit configuration',
    'panels:read',
    'RL-1057',
  ),
  panelUsage: page(
    'panelUsage',
    '/finance/panel-usage',
    'Patient panel usage',
    'claims:read',
    'RL-1059',
  ),
  panelReceivables: page(
    'panelReceivables',
    '/finance/panel-receivables',
    'Panel receivables',
    'claims:read',
    'RL-1060',
  ),
  onlinePayments: page(
    'onlinePayments',
    '/finance/online-payments',
    'Online payments',
    'online_payments:read',
    'RL-1341',
  ),
  claims: page('claims', '/finance/claims', 'Claims', 'claims:read'),
  monthlyInvoices: page(
    'monthlyInvoices',
    '/finance/invoices',
    'Monthly invoices',
    'invoices:read',
  ),
  ageing: page(
    'ageing',
    '/finance/ageing',
    'Outstanding / ageing',
    'ageing:read',
  ),
  remittances: page(
    'remittances',
    '/finance/remittances',
    'Remittances',
    'remittances:read',
  ),
  knockOff: page(
    'knockOff',
    '/finance/knock-off',
    'Knock-off',
    'knock_off:read',
  ),
  memberVerification: page(
    'memberVerification',
    '/finance/member-verification',
    'Member verification',
    'member_verification:read',
  ),
  books: page('books', '/finance/books', 'Books & payouts', 'books:read'),
  doctorRevenue: page(
    'doctorRevenue',
    '/doctor-revenue',
    'Doctor revenue',
    'revenue:read',
    'RL-1063',
  ),
  financeLocumPay: page(
    'financeLocumPay',
    '/finance/locum-pay',
    'Locum payouts',
    'locum_pay:pay',
    'RL-1056',
  ),
  reports: page('reports', '/reports', 'Reports', 'reports:read'),
  // Clinic manager
  branches: page(
    'branches',
    '/setup/branches',
    'Branches & rooms',
    'branches:read',
    'RL-548',
  ),
  services: page(
    'services',
    '/setup/services',
    'Services & prices',
    'services:read',
  ),
  rosters: page(
    'rosters',
    '/setup/rosters',
    'Doctors & rosters',
    'rosters:read',
  ),
  consentTemplates: page(
    'consentTemplates',
    '/setup/consent-templates',
    'Consent templates',
    'consent_templates:manage',
    'RL-1136',
  ),
  // RL-1886 (decision D24): the one Weekly hours page (was Doctor schedules
  // and the roster editor on Doctors & rosters).
  managerSchedules: page(
    'managerSchedules',
    '/setup/weekly-hours',
    'Weekly hours',
    'schedules:manage',
    'RL-1053',
  ),
  managerAttendance: page(
    'managerAttendance',
    '/setup/attendance',
    'Attendance',
    'attendance:manage',
    'RL-1054',
  ),
  locums: page('locums', '/setup/locums', 'Locums', 'locums:read', 'RL-1055'),
  managerLocumPay: page(
    'managerLocumPay',
    '/setup/locum-pay',
    'Locum pay',
    'locum_pay:approve',
    'RL-1056',
  ),
  discounts: page(
    'discounts',
    '/setup/discounts',
    'Discount schemes',
    'discounts:read',
  ),
  auditLog: page(
    'auditLog',
    '/audit-log',
    'Audit log',
    'audit_log:read',
    'RL-553',
  ),
  compliance: page(
    'compliance',
    '/compliance',
    'Compliance',
    'compliance:read',
  ),
  dataSecurity: page(
    'dataSecurity',
    '/data-security',
    'Data & security',
    'data_security:read',
  ),
  approvals: page(
    'approvals',
    '/approvals',
    'Approvals',
    'approvals:read',
    'RL-552',
  ),
  // Staff administration
  staff: page('staff', '/admin/staff', 'Staff & roles', 'staff:read'),
  staffSessions: page(
    'staffSessions',
    '/staff/sessions',
    'Staff sessions',
    'sessions:revoke',
    'RL-1332',
  ),
  permissions: page(
    'permissions',
    '/admin/permissions',
    'Permission matrix',
    'permissions:read',
  ),
  integrations: page(
    'integrations',
    '/admin/integrations',
    'Integrations',
    'integrations:read',
  ),
  groupReports: page(
    'groupReports',
    '/admin/group-reports',
    'Group reports',
    'group_reports:read',
  ),
  goLive: page('goLive', '/admin/go-live', 'Go-live', 'go_live:read'),
  superAdminLocumPay: page(
    'superAdminLocumPay',
    '/admin/locum-pay',
    'Locum pay',
    'locum_pay:approve',
    'RL-1056',
  ),
  // RL-1673 (gap #4): the nurses answer chats from the patient app.
  patientChat: page(
    'patientChat',
    '/patient-chat',
    'Patient chat',
    'clinic_chat:read',
    'RL-1673',
  ),
  // Help and team (every role)
  teamMessages: page(
    'teamMessages',
    '/team-messages',
    'Team messages',
    'team_messages:read',
  ),
  help: page('help', '/help', 'Help', 'help:read'),
} satisfies Record<string, PageDef>;

export type PageId = keyof typeof PAGES;

export type NavSource = 'screenshot' | 'walkthrough-list' | 'granted';

export interface NavGroup {
  /** Group heading as shown in the sidebar (undefined for the top Dashboard item). */
  heading?: string;
  pages: PageId[];
  /**
   * Where the grouping comes from: 'screenshot' = visible in the Admin v2
   * screenshots; 'walkthrough-list' = below the screenshot fold, so the order
   * follows the walkthrough's per-role page table. Verify with design.
   * 'granted' = RL-1818: pages a permission granted beyond the role's menu.
   */
  source: NavSource;
}

const HELP_AND_TEAM: NavGroup = {
  heading: 'Help and team',
  pages: ['teamMessages', 'help'],
  source: 'screenshot',
};
const TOP: NavGroup = { pages: ['dashboard'], source: 'screenshot' };

/** Sidebar per role, in Admin v2 order. */
export const NAVIGATION: Record<Role, NavGroup[]> = {
  front_desk: [
    TOP,
    {
      heading: 'Front desk',
      source: 'screenshot',
      pages: [
        'queue',
        'calendar',
        'waitingRoom',
        'registerPatient',
        'patients',
        'billing',
        'governmentSchemes',
        'cashDrawer',
        'medicalCertificates',
        'complaints',
        'messages',
        'medicals',
        'attendance',
      ],
    },
    HELP_AND_TEAM,
  ],
  doctor: [
    TOP,
    {
      heading: 'Clinical',
      source: 'screenshot',
      pages: [
        'inbox',
        'myPatients',
        'mySchedule',
        'myRevenue',
        'attendance',
        'teleconsults',
        'videoConsults',
        'resultsToReview',
        'patients',
        'medicalCertificates',
        'chronicCare',
        'mothersChildren',
        'safety',
        'medicals',
        'consentTemplates',
      ],
    },
    HELP_AND_TEAM,
  ],
  locum: [
    TOP,
    {
      heading: 'Clinical',
      source: 'screenshot',
      pages: [
        'myPatients',
        'mySchedule',
        'attendance',
        'myContract',
        'myLocumPay',
        'myRevenue',
        'resultsToReview',
        'patients',
        'medicalCertificates',
      ],
    },
    HELP_AND_TEAM,
  ],
  mental_health: [
    TOP,
    {
      heading: 'Wellbeing',
      source: 'screenshot',
      pages: ['sessions', 'compartmentAccess', 'wellbeingSetup', 'attendance'],
    },
    HELP_AND_TEAM,
  ],
  nurse: [
    TOP,
    {
      heading: 'Nursing',
      source: 'walkthrough-list',
      // RL-1887 (decision D6): nurses have no weekly doctor schedule.
      pages: ['patientChat', 'patients', 'attendance'],
    },
    HELP_AND_TEAM,
  ],
  lab: [
    TOP,
    {
      heading: 'Lab',
      source: 'screenshot',
      pages: ['labOrders', 'labResults', 'attendance'],
    },
    HELP_AND_TEAM,
  ],
  dispenser: [
    TOP,
    {
      heading: 'Dispensary',
      source: 'screenshot',
      pages: ['prescriptions', 'stock', 'medicineLeaflets', 'attendance'],
    },
    HELP_AND_TEAM,
  ],
  finance: [
    TOP,
    {
      heading: 'Panel management',
      source: 'screenshot',
      pages: [
        'panels',
        'panelLimits',
        'panelUsage',
        'billing',
        'panelReceivables',
        'onlinePayments',
        'claims',
        'monthlyInvoices',
        'ageing',
        'remittances',
        'knockOff',
        'governmentSchemes',
      ],
    },
    { heading: 'Pricing', source: 'screenshot', pages: ['memberVerification'] },
    {
      heading: 'Counter',
      source: 'screenshot',
      pages: [
        'cashDrawer',
        'books',
        'doctorRevenue',
        'financeLocumPay',
        'reports',
        'attendance',
      ],
    },
    HELP_AND_TEAM,
  ],
  clinic_manager: [
    TOP,
    {
      heading: 'Clinic setup',
      source: 'screenshot',
      pages: [
        'branches',
        'calendar',
        'services',
        'rosters',
        'managerSchedules',
        'managerAttendance',
        'locums',
        'discounts',
        'stock',
        'medicineLeaflets',
        'consentTemplates',
      ],
    },
    {
      heading: 'Governance',
      source: 'screenshot',
      pages: [
        'auditLog',
        'compliance',
        'complaints',
        'medicalCertificates',
        'patients',
        'messages',
        // RL-1673 review: the manager holds clinic_chat:read/reply (cover
        // and oversight, API batch 16 decision), so the chats are in the menu.
        'patientChat',
        'safety',
        'dataSecurity',
        'staffSessions',
        'doctorsInbox',
      ],
    },
    // Screenshot shows the heading and Approvals; Cash drawer and Reports follow
    // the walkthrough's page table (below the screenshot fold).
    {
      heading: 'Money',
      source: 'walkthrough-list',
      // RL-1187: managers read Panels, Panel receivables and Billing
      // (panels:read, claims:read, billing:read); changes stay with finance.
      pages: [
        'panels',
        'panelReceivables',
        'billing',
        'doctorRevenue',
        'approvals',
        'managerLocumPay',
        'cashDrawer',
        'reports',
      ],
    },
    { ...HELP_AND_TEAM, source: 'walkthrough-list' },
  ],
  super_admin: [
    TOP,
    {
      heading: 'Group',
      source: 'screenshot',
      pages: [
        'staff',
        // RL-1821 (gap #152): sessions:revoke is the Super admin's too.
        'staffSessions',
        'permissions',
        'integrations',
        'groupReports',
        'auditLog',
        'books',
        'doctorRevenue',
        'reports',
        'dataSecurity',
        'goLive',
      ],
    },
    // Screenshot shows Oversight down to Patients; the rest follows the
    // walkthrough's page table (below the screenshot fold).
    {
      heading: 'Oversight',
      source: 'walkthrough-list',
      pages: [
        'panels',
        'panelUsage',
        'panelReceivables',
        'claims',
        'monthlyInvoices',
        'knockOff',
        'branches',
        'managerSchedules',
        'managerAttendance',
        'locums',
        'superAdminLocumPay',
        'stock',
        'medicineLeaflets',
        'patients',
        'medicalCertificates',
        'approvals',
        'complaints',
        'messages',
        'safety',
        'compliance',
      ],
    },
    { ...HELP_AND_TEAM, source: 'walkthrough-list' },
  ],
};

/** Roles whose sidebar contains the page: used as the route's access list. */
export function rolesForPage(id: PageId): Role[] {
  const directRoles = PAGES[id].directRoles ?? [];
  return (Object.keys(NAVIGATION) as Role[]).filter(
    (role) =>
      directRoles.includes(role) ||
      NAVIGATION[role].some((group) => group.pages.includes(id)),
  );
}

export function pagesForRole(role: Role): PageId[] {
  return NAVIGATION[role].flatMap((group) => group.pages);
}

/**
 * RL-1818 (decision D29): pages the role's own permission bundle already opens
 * that the Admin v2 sidebar leaves out on purpose (the role reads that data
 * inside another page). Granting any other permission adds its page.
 */
export const LEFT_OUT_OF_MENU: Partial<Record<Role, PageId[]>> = {
  // Doctors read bills in the consultation; the counter takes payment.
  doctor: ['billing'],
  // Nurses see arrivals through Patients and vitals, not the counter queue.
  nurse: ['queue'],
  // Managers read panels and claims in Panels and Panel receivables.
  clinic_manager: ['panelLimits', 'panelUsage', 'claims'],
  super_admin: ['panelLimits'],
};

/** Heading of the group that lists pages granted beyond the role's menu. */
export const MORE_PAGES_HEADING = 'More pages';

/**
 * Pages a permission opens for this role that its Admin v2 sidebar does not
 * list: in PAGES order, only pages that open from the menu (no record id, not
 * direct-only), and not a second page with a label already in the menu (the
 * manager's and the Super admin's Locum pay, own and team Attendance).
 */
function grantedExtras(
  role: Role | string,
  shown: readonly PageId[],
  granted: ReadonlySet<string>,
): PageId[] {
  const inMenu = new Set<PageId>(pagesForRole(role as Role));
  const leftOut = new Set(LEFT_OUT_OF_MENU[role as Role] ?? []);
  const labels = new Set(shown.map((id) => PAGES[id].label));
  const extras: PageId[] = [];
  for (const id of Object.keys(PAGES) as PageId[]) {
    const p: PageDef = PAGES[id];
    if (
      inMenu.has(id) ||
      leftOut.has(id) ||
      p.permission === null ||
      !granted.has(p.permission) ||
      p.path.includes(':') ||
      p.directRoles ||
      labels.has(p.label)
    )
      continue;
    labels.add(p.label);
    extras.push(id);
  }
  return extras;
}

/**
 * Sidebar for the signed-in staff member: the Admin v2 grouping of the active
 * role, keeping only pages whose capability the API granted. Empty groups drop.
 * RL-1818: a page whose permission was granted to the role but which is not in
 * its Admin v2 menu is added under "More pages", above Help and team.
 */
export function visibleNavigation(
  role: Role | string,
  permissions: readonly string[],
): NavGroup[] {
  const granted = new Set(permissions);
  const groups = (NAVIGATION[role as Role] ?? [])
    .map((group) => ({
      ...group,
      pages: group.pages.filter((id) => {
        const permission = PAGES[id].permission;
        return permission === null || granted.has(permission);
      }),
    }))
    .filter((group) => group.pages.length > 0);
  if (groups.length === 0) return groups;
  const extras = grantedExtras(
    role,
    groups.flatMap((g) => g.pages),
    granted,
  );
  if (extras.length === 0) return groups;
  const more: NavGroup = {
    heading: MORE_PAGES_HEADING,
    pages: extras,
    source: 'granted',
  };
  const help = groups.findIndex((g) => g.heading === HELP_AND_TEAM.heading);
  const at = help === -1 ? groups.length : help;
  return [...groups.slice(0, at), more, ...groups.slice(at)];
}
